import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layers, Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { nome: "", descrizione: "", ordine: 0 };

export default function Categories() {
  const { hasPerm } = useAuth();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { const res = await api.get("/categories"); setCats(res.data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, ordine: cats.length }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ nome: c.nome, descrizione: c.descrizione || "", ordine: c.ordine || 0 }); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) { toast.error("Nome categoria obbligatorio"); return; }
    const payload = { ...form, ordine: parseInt(form.ordine) || 0 };
    setSaving(true);
    try {
      if (editing) { await api.put(`/categories/${editing.id}`, payload); toast.success("Categoria aggiornata"); }
      else { await api.post("/categories", payload); toast.success("Categoria creata"); }
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    try { await api.delete(`/categories/${c.id}`); toast.success("Categoria eliminata"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Base per le tariffe di noleggio</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">CATEGORIE BICI</h1>
        </div>
        {hasPerm("categories.create") && (
          <button onClick={openNew} data-testid="add-category-btn"
            className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
            <Plus className="h-5 w-5" /> Nuova categoria
          </button>
        )}
      </div>

      {cats.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">
          Nessuna categoria. Creane una (es. "Front", "Full", "Top di gamma").
        </div>
      ) : (
        <div className="space-y-2">
          {cats.map((c) => (
            <div key={c.id} data-testid={`category-row-${c.id}`}
              className="bg-[#141414] border border-zinc-800 rounded-sm p-4 flex items-center gap-4 hover:border-[#f98125]/40 transition-colors">
              <div className="flex items-center gap-2 text-zinc-600">
                <GripVertical className="h-4 w-4" />
                <span className="font-display text-2xl text-[#f98125] w-8 text-center">{c.ordine}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{c.nome}</div>
                {c.descrizione && <div className="text-xs text-zinc-500 truncate">{c.descrizione}</div>}
              </div>
              <span className="text-xs text-zinc-500 whitespace-nowrap">{c.bike_count} bici</span>
              <div className="flex items-center gap-1">
                {hasPerm("categories.edit") && (
                  <button onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                )}
                {hasPerm("categories.delete") && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-category-${c.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#141414] border-zinc-800">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare "{c.nome}"?</AlertDialogTitle>
                        <AlertDialogDescription>Le {c.bike_count} bici associate resteranno senza categoria. Azione irreversibile.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(c)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Layers className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica categoria" : "Nuova categoria"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label>
              <Input data-testid="category-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" placeholder="es. Front / Full / Top di gamma" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Descrizione</Label>
              <Textarea data-testid="category-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Ordine di visualizzazione</Label>
              <Input data-testid="category-ordine" type="number" value={form.ordine} onChange={(e) => setForm({ ...form, ordine: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm w-32" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="category-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-50">{saving ? "Salvataggio..." : "Salva"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
